import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { type BreadcrumbItem } from "@/components/layout/breadcrumb-defs";
import { CloneRecipeDialog } from "@/components/recipes/CloneRecipeDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ViewToggle } from "@/components/ui/view-toggle";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useViewPreference } from "@/hooks/use-view-preference";
import {
  updateRecipeBox,
  useRecipeBox,
  useRecipeBoxes,
  useRecipeBoxInvitations,
  useRecipes,
} from "@/hooks/use-recipe-boxes";
import { useToast } from "@/hooks/use-toast";
import { timestampToDate } from "@/lib/date-utils";
import { trackMemberInvited, trackMemberRemoved, trackMemberRoleChanged } from "@/lib/analytics-service";
import {
  cancelRecipeBoxInvitation,
  inviteRecipeBoxMember,
  removeRecipeBoxMember,
  resendRecipeBoxInvitation,
  updateRecipeBoxMemberRole,
} from "@/lib/recipe-box-invitations-service";
import { deleteRecipeBox } from "@/lib/recipe-box-service";
import { canAddRecipes, canExportRecipes, canManageMembers, getUserRoleInBox } from "@/lib/recipe-box-permissions";
import { exportRecipesToCSV, exportRecipesToJSON } from "@/lib/recipe-export-service";
import { cloneRecipe, cloneRecipes, deleteRecipe } from "@/lib/recipe-service";
import type { InvitationStatus, Recipe, RecipeBoxInvitation, RecipeBoxMember, RecipeBoxRole } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { CategoryFilter } from "./recipe-box/_components/CategoryFilter";
import { CustomCategoriesDialog } from "./recipe-box/_components/CustomCategoriesDialog";
import { MembersDialog } from "./recipe-box/_components/MembersDialog";
import { MobileActionsMenu } from "./recipe-box/_components/MobileActionsMenu";
import type { MemberPreviewItem } from "./recipe-box/_components/MembersPreview";
import { RecipeBoxHeader } from "./recipe-box/_components/RecipeBoxHeader";
import { RecipeSearch } from "./recipe-box/_components/RecipeSearch";
import { RecipesSection } from "./recipe-box/_components/RecipesSection";
import { UnifiedSearch } from "./recipe-box/_components/UnifiedSearch";
import { normalizeInvitationStatus } from "./recipe-box/_lib/invitations";
import { EditRecipeBoxDialog } from "./home/_components/EditRecipeBoxDialog";
import { recipeBoxBaseSchema, type RecipeBoxFormValues } from "./home/_lib/recipeBoxForm";

type InvitationAction = { id: string; type: "cancel" | "resend" } | null;

type NormalizedInvitation = RecipeBoxInvitation & { status: InvitationStatus };

export default function RecipeBoxPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { box, loading, error } = useRecipeBox(id);
  const {
    recipes,
    loading: recipesLoading,
    error: recipesError,
  } = useRecipes(id);
  const {
    invitations,
    loading: invitationsLoading,
    error: invitationsError,
  } = useRecipeBoxInvitations(id);
  const { user } = useAuthProfile();
  const { toast } = useToast();
  const { viewPreference, setViewPreference } = useViewPreference();

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    if (!box) {
      return [
        { label: "Recipe Boxes", href: "/home" },
        { label: "Recipe Box" },
      ];
    }

    return [
      { label: "Recipe Boxes", href: "/home" },
      { label: box.title || "Recipe Box" },
    ];
  }, [box]);

  usePageBreadcrumbs(breadcrumbItems);

  const [recipePendingDelete, setRecipePendingDelete] = useState<Recipe | null>(null);
  const [isDeletingRecipe, setIsDeletingRecipe] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState<RecipeBoxRole>("member");
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [activeInvitationAction, setActiveInvitationAction] = useState<InvitationAction>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [changingRoleMemberId, setChangingRoleMemberId] = useState<string | null>(null);
  const [roleChangeDialog, setRoleChangeDialog] = useState<{
    open: boolean;
    member: RecipeBoxMember | null;
    newRole: RecipeBoxRole | null;
  }>({ open: false, member: null, newRole: null });
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [recipeToClone, setRecipeToClone] = useState<Recipe | null>(null);
  const [isCloning, setIsCloning] = useState(false);
  const [selectedRecipesForBulkClone, setSelectedRecipesForBulkClone] = useState<Set<string>>(new Set());
  const [bulkCloneDialogOpen, setBulkCloneDialogOpen] = useState(false);
  const [isBulkCloning, setIsBulkCloning] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [labelsDialogOpen, setLabelsDialogOpen] = useState(false);
  const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>(recipes);
  const [editBoxDialogOpen, setEditBoxDialogOpen] = useState(false);
  const [editCoverFile, setEditCoverFile] = useState<File | null>(null);
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [deleteBoxDialogOpen, setDeleteBoxDialogOpen] = useState(false);
  const [isDeletingBox, setIsDeletingBox] = useState(false);

  const editForm = useForm<RecipeBoxFormValues>({
    resolver: zodResolver(recipeBoxBaseSchema),
    defaultValues: { title: "", description: "" },
  });

  // Update filtered recipes when recipes change
  useEffect(() => {
    setFilteredRecipes(recipes);
  }, [recipes]);

  const { boxes: userRecipeBoxes } = useRecipeBoxes(user?.uid);

  if (!id) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
        <p className="font-headline text-lg">Recipe box ID is missing.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-64 w-full rounded-lg" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-40" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-destructive">
        <p>{error}</p>
      </div>
    );
  }

  if (!box) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
        <p className="font-headline text-lg">Recipe box not found.</p>
        <p className="mt-2 text-sm text-muted-foreground">It may have been deleted or you may not have access.</p>
      </div>
    );
  }

  const members = box.members ?? [];
  const now = Date.now();
  const normalizedInvitations: NormalizedInvitation[] = invitations.map((invite) => {
    let status = normalizeInvitationStatus(invite.status);
    if (status === "pending" && invite.expiresAt) {
      const expiryDate = timestampToDate(invite.expiresAt);
      if (expiryDate && expiryDate.getTime() < now) {
        status = "expired";
      }
    }
    return { ...invite, status };
  });
  const pendingInvites = normalizedInvitations.filter(
    (invite) => invite.status === "pending" || invite.status === "expired",
  );
  const userRole = getUserRoleInBox(box, user?.uid ?? null);
  const isOwner = userRole === "owner";
  const canAdd = canAddRecipes(box, user?.uid ?? null);
  const canManage = canManageMembers(box, user?.uid ?? null);
  const canExport = canExportRecipes(box, user?.uid ?? null);

  const acceptedMemberPreviews = members.map<MemberPreviewItem>((member) => ({
    id: member.uid,
    type: "member",
    name: member.displayName ?? member.email ?? "Member",
    email: member.email,
    photoURL: member.photoURL,
  }));

  const pendingInvitePreviews = pendingInvites.map<MemberPreviewItem>((invite) => ({
    id: invite.id,
    type: "pending",
    name: invite.recipientName ?? invite.recipientEmail ?? invite.invitedUserUid ?? "Pending member",
    email: invite.recipientEmail,
    photoURL: undefined,
  }));

  const memberPreviewItems = [...acceptedMemberPreviews, ...pendingInvitePreviews].slice(0, 5);
  const totalMemberCount = members.length + pendingInvites.length;
  const remainingPreviewCount = Math.max(totalMemberCount - memberPreviewItems.length, 0);

  const handleMembersDialogChange = (open: boolean) => {
    setMembersDialogOpen(open);
    if (!open) {
      setInviteEmail("");
      setInviteName("");
      setInviteRole("member");
      setActiveInvitationAction(null);
      setRemovingMemberId(null);
      setChangingRoleMemberId(null);
    }
  };

  const handleInviteSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedEmail = inviteEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      toast({
        variant: "destructive",
        title: "Email required",
        description: "Enter an email address to send an invitation.",
      });
      return;
    }

    setIsSendingInvite(true);
    try {
      await inviteRecipeBoxMember({
        recipeBoxId: box.id,
        email: normalizedEmail,
        recipientName: inviteName.trim() || undefined,
        role: inviteRole
      });
      trackMemberInvited(inviteRole);
      const recipientDisplay = inviteName.trim() || normalizedEmail;
      toast({
        title: "Invitation sent",
        description: `We invited ${recipientDisplay} to this recipe box.`,
      });
      setInviteEmail("");
      setInviteName("");
    } catch (inviteError) {
      console.error("Failed to send invitation", inviteError);
      const message = inviteError instanceof Error ? inviteError.message : "Please try again.";
      toast({
        variant: "destructive",
        title: "Could not send invitation",
        description: message,
      });
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleCancelInvitation = async (invitation: RecipeBoxInvitation) => {
    if (!invitation.id || !isOwner) {
      return;
    }

    setActiveInvitationAction({ id: invitation.id, type: "cancel" });
    try {
      await cancelRecipeBoxInvitation(invitation.id);
      toast({
        title: "Invitation cancelled",
        description: `Revoked the invite for ${invitation.recipientEmail ?? "that user"}.`,
      });
    } catch (cancelError) {
      console.error("Failed to cancel invitation", cancelError);
      const message = cancelError instanceof Error ? cancelError.message : "Please try again.";
      toast({
        variant: "destructive",
        title: "Could not cancel invitation",
        description: message,
      });
    } finally {
      setActiveInvitationAction(null);
    }
  };

  const handleResendInvitation = async (invitation: RecipeBoxInvitation) => {
    if (!invitation.id || !isOwner) {
      return;
    }

    setActiveInvitationAction({ id: invitation.id, type: "resend" });
    try {
      await resendRecipeBoxInvitation(invitation.id);
      toast({
        title: "Invitation extended",
        description: `Expiration extended for ${invitation.recipientEmail ?? "that user"}.`,
      });
    } catch (resendError) {
      console.error("Failed to resend invitation", resendError);
      const message = resendError instanceof Error ? resendError.message : "Please try again.";
      toast({
        variant: "destructive",
        title: "Could not resend invitation",
        description: message,
      });
    } finally {
      setActiveInvitationAction(null);
    }
  };

  const handleRemoveMember = async (member: RecipeBoxMember) => {
    if (!isOwner || member.uid === user?.uid) {
      return;
    }

    setRemovingMemberId(member.uid);
    try {
      await removeRecipeBoxMember(box.id, member.uid);
      trackMemberRemoved();
      toast({
        title: "Member removed",
        description: `${member.displayName ?? member.email ?? "The member"} no longer has access.`,
      });
    } catch (removeError) {
      console.error("Failed to remove member", removeError);
      const message = removeError instanceof Error ? removeError.message : "Please try again.";
      toast({
        variant: "destructive",
        title: "Could not remove member",
        description: message,
      });
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleRoleChange = (member: RecipeBoxMember, newRole: RecipeBoxRole) => {
    if (member.role === newRole) {
      return;
    }
    setRoleChangeDialog({ open: true, member, newRole });
  };

  const handleConfirmRoleChange = async () => {
    if (!roleChangeDialog.member || !roleChangeDialog.newRole || !canManage) {
      return;
    }

    setChangingRoleMemberId(roleChangeDialog.member.uid);
    try {
      await updateRecipeBoxMemberRole(box.id, roleChangeDialog.member.uid, roleChangeDialog.newRole);
      trackMemberRoleChanged(roleChangeDialog.member.role, roleChangeDialog.newRole);
      toast({
        title: "Member role updated",
        description: `${roleChangeDialog.member.displayName ?? roleChangeDialog.member.email ?? "The member"}'s role has been changed to ${roleChangeDialog.newRole}.`,
      });
      setRoleChangeDialog({ open: false, member: null, newRole: null });
    } catch (roleChangeError) {
      console.error("Failed to update member role", roleChangeError);
      const message = roleChangeError instanceof Error ? roleChangeError.message : "Please try again.";
      toast({
        variant: "destructive",
        title: "Could not update member role",
        description: message,
      });
    } finally {
      setChangingRoleMemberId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!recipePendingDelete) {
      return;
    }

    setIsDeletingRecipe(true);
    try {
      await deleteRecipe(box.id, recipePendingDelete.id, recipePendingDelete.imageStoragePath, box, user?.uid ?? null);
      toast({
        title: "Recipe deleted",
        description: `${recipePendingDelete.title} has been removed from this box.`,
      });
    } catch (deleteError) {
      console.error("Failed to delete recipe", deleteError);
      const message = deleteError instanceof Error ? deleteError.message : "Please try again.";
      toast({
        variant: "destructive",
        title: "Could not delete recipe",
        description: message,
      });
    } finally {
      setIsDeletingRecipe(false);
      setRecipePendingDelete(null);
    }
  };

  const handleExportJSON = () => {
    if (recipes.length === 0) {
      toast({
        variant: "destructive",
        title: "No recipes to export",
        description: "This recipe box doesn't have any recipes yet.",
      });
      return;
    }
    exportRecipesToJSON(recipes, box);
    toast({
      title: "Export started",
      description: "Your recipes are being exported as JSON.",
    });
  };

  const handleExportCSV = () => {
    if (recipes.length === 0) {
      toast({
        variant: "destructive",
        title: "No recipes to export",
        description: "This recipe box doesn't have any recipes yet.",
      });
      return;
    }
    exportRecipesToCSV(recipes, box);
    toast({
      title: "Export started",
      description: "Your recipes are being exported as CSV.",
    });
  };



  const openEditBox = () => {
    if (!box) return;
    setEditFormError(null);
    setEditCoverFile(null);
    editForm.reset({ title: box.title, description: box.description ?? "" });
    setEditBoxDialogOpen(true);
  };

  const closeEditBox = () => {
    setEditBoxDialogOpen(false);
    setEditFormError(null);
    setEditCoverFile(null);
    editForm.reset({ title: "", description: "" });
  };

  const handleEditBoxSubmit = editForm.handleSubmit(async (values) => {
    if (!user || !box) {
      setEditFormError("Unable to update this recipe box right now.");
      return;
    }
    try {
      setEditFormError(null);
      await updateRecipeBox(user.uid, box.id, {
        title: values.title,
        description: values.description?.trim() || "",
        newCoverFile: editCoverFile,
        currentImageStoragePath: box.imageStoragePath,
      });
      closeEditBox();
    } catch (error) {
      console.error("Failed to update recipe box", error);
      setEditFormError("Could not update recipe box. Please try again.");
    }
  });

  const handleDeleteBox = async () => {
    if (!box) return;
    setIsDeletingBox(true);
    try {
      await deleteRecipeBox(box.id);
      toast({ title: "Recipe box deleted", description: `${box.title} has been permanently deleted.` });
      navigate("/home");
    } catch (error) {
      console.error("Failed to delete recipe box", error);
      const message = error instanceof Error ? error.message : "Please try again.";
      toast({ variant: "destructive", title: "Could not delete recipe box", description: message });
    } finally {
      setIsDeletingBox(false);
      setDeleteBoxDialogOpen(false);
    }
  };

  const handleCloneDifferentBox = (recipe: Recipe) => {
    setRecipeToClone(recipe);
    setCloneDialogOpen(true);
  };

  const handleCloneToBox = async (targetRecipeBoxId: string) => {
    if (!recipeToClone || !user) {
      return;
    }

    setIsCloning(true);
    try {
      await cloneRecipe(recipeToClone.id, {
        newRecipeBoxId: targetRecipeBoxId,
        newOwnerUid: user.uid,
        titlePrefix: "Copy of ",
      });
      toast({
        title: "Recipe cloned",
        description: "The recipe has been cloned to the selected recipe box.",
      });
      setCloneDialogOpen(false);
      setRecipeToClone(null);
    } catch (cloneError) {
      console.error("Failed to clone recipe", cloneError);
      toast({
        variant: "destructive",
        title: "Could not clone recipe",
        description: "Please try again.",
      });
    } finally {
      setIsCloning(false);
    }
  };

  const handleBulkCloneToBox = async (targetRecipeBoxId: string) => {
    if (!user || selectedRecipesForBulkClone.size === 0) {
      return;
    }

    setIsBulkCloning(true);
    try {
      const recipeIds = Array.from(selectedRecipesForBulkClone);
      const clonedIds = await cloneRecipes(recipeIds, targetRecipeBoxId, user.uid);
      toast({
        title: "Recipes cloned",
        description: `${clonedIds.length} recipe${clonedIds.length === 1 ? "" : "s"} cloned to the selected recipe box.`,
      });
      setBulkCloneDialogOpen(false);
      setSelectedRecipesForBulkClone(new Set());
    } catch (cloneError) {
      console.error("Failed to bulk clone recipes", cloneError);
      toast({
        variant: "destructive",
        title: "Could not clone recipes",
        description: "Please try again.",
      });
    } finally {
      setIsBulkCloning(false);
    }
  };

  const handleRequestClone = (recipe: Recipe) => {
    // For now, just clone to different box (can enhance later with a menu)
    handleCloneDifferentBox(recipe);
  };

  return (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Mobile back button - shows on mobile when breadcrumbs are hidden */}
      <div className="md:hidden">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 mb-2"
          onClick={() => navigate("/home")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-2"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
          Back to Recipe Boxes
        </Button>
      </div>
      <RecipeBoxHeader
        title={box.title}
        description={box.description}
        canExport={canExport}
        onExportJSON={handleExportJSON}
        onExportCSV={handleExportCSV}
        isOwner={isOwner}
        onEditBox={openEditBox}
        onDeleteBox={() => setDeleteBoxDialogOpen(true)}
      />

      <div className="flex flex-wrap items-center gap-2 w-full">
        <h2 className="flex-1 min-w-0 font-headline text-2xl font-semibold">Recipes</h2>
        {/* Mobile: Single menu button, Desktop: Individual buttons */}
        <div className="hidden sm:flex items-center gap-2">
          <ViewToggle view={viewPreference} onViewChange={setViewPreference} />
          <MembersDialog
            isOpen={membersDialogOpen}
            onOpenChange={handleMembersDialogChange}
            recipeBox={box}
            members={members}
            pendingInvitations={pendingInvites}
            invitationsError={invitationsError}
            invitationsLoading={invitationsLoading}
            isOwner={canManage}
            inviteEmail={inviteEmail}
            inviteName={inviteName}
            onInviteEmailChange={setInviteEmail}
            onInviteNameChange={setInviteName}
            inviteRole={inviteRole}
            onInviteRoleChange={setInviteRole}
            onInviteSubmit={handleInviteSubmit}
            isSendingInvite={isSendingInvite}
            memberPreviewItems={memberPreviewItems}
            remainingPreviewCount={remainingPreviewCount}
            totalMemberCount={totalMemberCount}
            currentUserId={user?.uid}
            onRemoveMember={handleRemoveMember}
            onRoleChange={handleRoleChange}
            removingMemberId={removingMemberId}
            changingRoleMemberId={changingRoleMemberId}
            onCancelInvitation={handleCancelInvitation}
            onResendInvitation={handleResendInvitation}
            activeInvitationAction={activeInvitationAction}
          />
          {canAdd ? (
            <Button size="sm" asChild className="gap-1">
              <Link to={`/add-recipe?recipeBoxId=${box.id}`}>
                <PlusCircle className="h-3.5 w-3.5" />
                Add Recipe
              </Link>
            </Button>
          ) : null}
        </div>
        {/* Mobile menu */}
        <MobileActionsMenu
          viewPreference={viewPreference}
          onViewChange={setViewPreference}
          onManageCategories={() => setLabelsDialogOpen(true)}
          onOpenMembers={() => setMembersDialogOpen(true)}
          canManage={canManage}
          isOwner={isOwner}
          canAdd={canAdd ?? false}
          recipeBoxId={box.id}
          onEditBox={openEditBox}
          onDeleteBox={() => setDeleteBoxDialogOpen(true)}
        />
      </div>

      {/* Mobile: Unified Search, Desktop: Separate Search + Filters */}
      <div className="sm:hidden">
        <UnifiedSearch
          recipes={recipes}
          recipeBox={box}
          onResultsChange={setFilteredRecipes}
          onCategorySelect={setSelectedLabel}
          selectedCategory={selectedLabel}
        />
      </div>

      <div className="hidden sm:block space-y-4">
        {/* Search and Filters */}
        <RecipeSearch
          recipes={recipes}
          recipeBox={box}
          onResultsChange={setFilteredRecipes}
        />

        {/* Category Filter */}
        <div className="flex items-center justify-between w-full overflow-x-hidden">
          <CategoryFilter
            recipeBox={box}
            selectedLabel={selectedLabel}
            onLabelChange={setSelectedLabel}
          />
          {canManage && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLabelsDialogOpen(true)}
            >
              Manage Labels
            </Button>
          )}
        </div>
      </div>

      <RecipesSection
        recipes={selectedLabel
          ? filteredRecipes.filter((recipe) => recipe.labels.includes(selectedLabel))
          : filteredRecipes}
        recipeBox={box}
        userId={user?.uid ?? null}
        recipeBoxId={box.id}
        loading={recipesLoading}
        error={recipesError}
        viewPreference={viewPreference}
        onRequestDelete={(recipe) => setRecipePendingDelete(recipe)}
        onRequestClone={handleRequestClone}
      />

      <AlertDialog
        open={recipePendingDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeletingRecipe) {
            setRecipePendingDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The recipe will be removed from the recipe box.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingRecipe}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} disabled={isDeletingRecipe}>
              {isDeletingRecipe ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={roleChangeDialog.open}
        onOpenChange={(open) => {
          if (!open && !changingRoleMemberId) {
            setRoleChangeDialog({ open: false, member: null, newRole: null });
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change member role?</AlertDialogTitle>
            <AlertDialogDescription>
              {roleChangeDialog.member && roleChangeDialog.newRole ? (
                <>
                  Change <strong>{roleChangeDialog.member.displayName ?? roleChangeDialog.member.email ?? "this member"}</strong>&apos;s role from{" "}
                  <strong className="uppercase">{roleChangeDialog.member.role}</strong> to{" "}
                  <strong className="uppercase">{roleChangeDialog.newRole}</strong>?
                  {roleChangeDialog.newRole === "owner" && (
                    <span className="block mt-2 text-sm font-medium text-amber-600 dark:text-amber-400">
                      Note: Owners have full control and can manage all members.
                    </span>
                  )}
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={changingRoleMemberId !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRoleChange} disabled={changingRoleMemberId !== null}>
              {changingRoleMemberId ? "Updating..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {recipeToClone && (
        <CloneRecipeDialog
          open={cloneDialogOpen}
          onOpenChange={(open) => {
            setCloneDialogOpen(open);
            if (!open) {
              setRecipeToClone(null);
            }
          }}
          recipeTitle={recipeToClone.title}
          sourceRecipeBoxId={recipeToClone.recipeBoxId}
          recipeBoxes={userRecipeBoxes}
          userId={user?.uid ?? null}
          onClone={handleCloneToBox}
          isCloning={isCloning}
        />
      )}

      {selectedRecipesForBulkClone.size > 0 && (
        <CloneRecipeDialog
          open={bulkCloneDialogOpen}
          onOpenChange={(open) => {
            setBulkCloneDialogOpen(open);
            if (!open) {
              setSelectedRecipesForBulkClone(new Set());
            }
          }}
          recipeTitle={`${selectedRecipesForBulkClone.size} recipe${selectedRecipesForBulkClone.size === 1 ? "" : "s"}`}
          sourceRecipeBoxId={box.id}
          recipeBoxes={userRecipeBoxes}
          userId={user?.uid ?? null}
          onClone={handleBulkCloneToBox}
          isCloning={isBulkCloning}
        />
      )}

      <CustomCategoriesDialog
        open={labelsDialogOpen}
        onOpenChange={setLabelsDialogOpen}
        recipeBox={box}
        userId={user?.uid ?? null}
        onUpdate={() => {
          // Recipe box will update automatically via subscription
        }}
      />

      <EditRecipeBoxDialog
        isOpen={editBoxDialogOpen}
        onOpenChange={(open) => { if (!open) closeEditBox(); }}
        form={editForm}
        isSubmitting={editForm.formState.isSubmitting}
        errorMessage={editFormError}
        onSubmit={handleEditBoxSubmit}
        onFileChange={setEditCoverFile}
        recipeBox={box}
        onCancel={closeEditBox}
        selectedFileName={editCoverFile?.name}
        onCropSave={async (focalPointX, focalPointY, zoom) => {
          if (!user || !box) return;
          try {
            await updateRecipeBox(user.uid, box.id, {
              imageFocalPointX: focalPointX,
              imageFocalPointY: focalPointY,
              imageZoom: zoom,
            });
            toast({
              title: "Image position updated",
              description: "Your crop has been saved.",
            });
          } catch (err) {
            console.error("Failed to save crop", err);
            toast({
              variant: "destructive",
              title: "Could not save position",
              description: "Please try again.",
            });
          }
        }}
      />

      <AlertDialog
        open={deleteBoxDialogOpen}
        onOpenChange={(open) => { if (!open && !isDeletingBox) setDeleteBoxDialogOpen(false); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this recipe box?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{box.title}</strong> and all of its recipes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingBox}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBox} disabled={isDeletingBox}>
              {isDeletingBox ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

